import { Chip, TextArea, Typography } from '@navi/web-ui/lib/primitives';
import { Controller, useForm } from 'react-hook-form';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { capitalizeFirstLetter } from '@cp/src/pages/AllAgents/AgentForm/utils/utils';
import {
  getActivityLogs,
  getAnomalyTickets,
  updateAnomaly
} from '../../actions/AnomalyTrackerAction';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import { COMMENT_VALIDATIONS } from '../../constants';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { AnomalyStatus, AnomalyTableKeys, IOption, IRcaDefaultValues } from '../../types';
import { CircularLoaderIcon } from '@navi/web-ui/lib/icons';
import AnomalySubmitCta from '../ETA/AnomalySubmitCta';
import CurrentRca from './CurrentRca';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

const RCA = () => {
  const dispatch = useDispatch();
  const params = readQueryParams();
  const selectedAnomalyId = params?.[AnomalyTableKeys.ANOMALY_TABLE_ACTION]?.selectedId;

  const anomalyDetails = useSelector(
    (state: RootState) => state?.anomalyTracker?.anomalyDetails?.data
  );
  const isLoading = useSelector(
    (state: RootState) => state?.anomalyTracker?.anomalyDetails?.loading
  );
  const activityLogsLoading = useSelector(
    (state: RootState) => state?.anomalyTracker?.acitvityLogs?.loading
  );
  const rcaReasons = useSelector((state: RootState) => state?.anomalyTracker?.rcaReasons?.[0]);

  const {
    watch,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<IRcaDefaultValues>({
    defaultValues: {
      reason: '',
      comment: ''
    }
  });

  const reason = watch('reason');

  const successCallback = () => {
    dispatch(getActivityLogs(selectedAnomalyId));
    dispatch(getAnomalyTickets(AnomalyStatus.OPEN, params?.openAnomalyTable, false));
    reset();
  };

  const submitRCA = (data: IRcaDefaultValues) => {
    addClickstreamEvent(
      CLICKSTREAM_EVENT_NAMES.LH_ANOMALY_TRACKER_ANOMALY_RCA_SUBMIT_BUTTON_CLICKED,
      {
        anomalyId: selectedAnomalyId,
        data
      }
    );
    dispatch(updateAnomaly(selectedAnomalyId, { rca: data }, successCallback));
  };

  if (activityLogsLoading || isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-6 w-[320px]">
        <CircularLoaderIcon size="lg" />
      </div>
    );
  }

  return (
    <>
      <form className="overflow-scroll m-0">
        <CurrentRca />
        <div className="p-6 flex flex-col gap-5">
          <div className="flex flex-col gap-3 mb-2">
            <Typography color="var(--navi-color-gray-c1)" variant={'h4'}>
              Update RCA
            </Typography>

            <SuspenseLoader
              loading={isLoading}
              fallBack={
                <LineLoader
                  numberOfLines={1}
                  childrenType="P3"
                  dimenshionUnit="px"
                  lineWidth={100}
                />
              }
            >
              <Typography as="span" color="var(--grayscale-2)" variant={'p4'}>
                Anomaly Type:{' '}
                <Typography as="span" color="var(--grayscale-2)" variant={'p4'}>
                  {capitalizeFirstLetter(anomalyDetails?.anomalyDetectedOn || '')}
                </Typography>
              </Typography>
            </SuspenseLoader>
          </div>
          <div>
            <div className="flex items-center mb-2">
              <Typography color="var(--grayscale-2)" variant={'p4'}>
                {rcaReasons?.label}
                <RequiredIcon className="mb-1 ml-1" />
              </Typography>
            </div>

            <Controller
              control={control}
              name="reason"
              render={({ field: { onChange, value } }) => (
                <div className="">
                  {rcaReasons?.options?.map((option: IOption) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      selected={value === option?.value}
                      onChipClick={() => {
                        onChange(option?.value);
                      }}
                      labelClasses="text-start"
                      wrapperClasses="mr-2 mb-2"
                    />
                  ))}
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name="comment"
            rules={COMMENT_VALIDATIONS}
            render={({ field: { onChange, value } }) => (
              <TextArea
                label="Comments (If any)"
                rows={5}
                containerClassName="w-full"
                placeholder="Type here..."
                onChange={onChange}
                value={value}
                error={errors?.comment?.message}
              />
            )}
          />
        </div>
      </form>
      <AnomalySubmitCta disabled={!reason} onSubmit={handleSubmit(submitRCA)} />
    </>
  );
};

export default RCA;
